import { useState, type MouseEvent } from 'react';
import { ArrowDown, ArrowUp, Download, Lock, MoreVertical } from 'lucide-react';
import type { Nodo } from '@doc/shared';
import { fechaRelativa, iconoDe, tamano } from '../../lib/formato.js';
import { urlArchivo } from '../../lib/api.js';

export type CampoOrden = 'nombre' | 'bytes' | 'actualizadoEn';
export interface Orden {
  campo: CampoOrden;
  ascendente: boolean;
}

interface Props {
  nodos: Nodo[];
  vista: 'lista' | 'cuadricula';
  seleccion: Set<string>;
  cortados: Set<string>;
  orden: Orden;
  alOrdenar(campo: CampoOrden): void;
  alClicear(evento: MouseEvent, nodo: Nodo, indice: number): void;
  alAbrir(nodo: Nodo): void;
  alMenu(evento: MouseEvent, nodo: Nodo | null): void;
  alSoltarEn(ids: string[], destinoId: string): void;
  alDescargar(nodo: Nodo): void;
}

export function ordenar(nodos: Nodo[], orden: Orden): Nodo[] {
  const signo = orden.ascendente ? 1 : -1;
  return [...nodos].sort((a, b) => {
    // Las carpetas siempre arriba: es la convención de todos los exploradores
    // y evita tener que buscarlas entre cien archivos.
    if (a.tipo !== b.tipo) return a.tipo === 'CARPETA' ? -1 : 1;
    if (orden.campo === 'nombre') return signo * a.nombre.localeCompare(b.nombre, 'es', { numeric: true });
    if (orden.campo === 'bytes') return signo * ((a.bytes ?? 0) - (b.bytes ?? 0));
    return signo * (new Date(a.actualizadoEn).getTime() - new Date(b.actualizadoEn).getTime());
  });
}

export function ListaNodos(props: Props) {
  const { nodos, vista, seleccion, cortados, orden, alOrdenar, alClicear, alAbrir, alMenu, alSoltarEn, alDescargar } =
    props;
  const [destino, setDestino] = useState<string | null>(null);

  const arrastrar = (evento: React.DragEvent, nodo: Nodo) => {
    // Si se arrastra algo que no estaba seleccionado, manda ese elemento; si
    // no, se arrastra toda la selección.
    const ids = seleccion.has(nodo.id) ? [...seleccion] : [nodo.id];
    evento.dataTransfer.setData('application/x-nodos', JSON.stringify(ids));
    evento.dataTransfer.effectAllowed = 'move';
  };

  const propsSoltar = (nodo: Nodo) =>
    nodo.tipo === 'CARPETA' && nodo.permisos.escribir
      ? {
          onDragOver: (e: React.DragEvent) => {
            if (!e.dataTransfer.types.includes('application/x-nodos')) return;
            e.preventDefault();
            e.stopPropagation();
            setDestino(nodo.id);
          },
          onDragLeave: () => setDestino((d) => (d === nodo.id ? null : d)),
          onDrop: (e: React.DragEvent) => {
            const carga = e.dataTransfer.getData('application/x-nodos');
            setDestino(null);
            if (!carga) return;
            e.preventDefault();
            e.stopPropagation();
            const ids = (JSON.parse(carga) as string[]).filter((id) => id !== nodo.id);
            if (ids.length > 0) alSoltarEn(ids, nodo.id);
          },
        }
      : {};

  if (vista === 'cuadricula') {
    return (
      <div className="lista" onContextMenu={(e) => alMenu(e, null)}>
        <div className="cuadricula">
          {nodos.map((nodo, indice) => {
            const { Icono, clase } = iconoDe(nodo);
            const esImagen = nodo.mime?.startsWith('image/');
            return (
              <div
                key={nodo.id}
                className={`tarjeta${seleccion.has(nodo.id) ? ' seleccionada' : ''}${
                  cortados.has(nodo.id) ? ' cortada' : ''
                }${destino === nodo.id ? ' destino' : ''}`}
                draggable
                onDragStart={(e) => arrastrar(e, nodo)}
                onClick={(e) => alClicear(e, nodo, indice)}
                onDoubleClick={() => alAbrir(nodo)}
                onContextMenu={(e) => alMenu(e, nodo)}
                {...propsSoltar(nodo)}
              >
                {esImagen ? (
                  <img className="tarjeta__miniatura" src={urlArchivo(nodo.id, 'vista')} alt="" loading="lazy" />
                ) : (
                  <div className={`tarjeta__icono icono-tipo ${clase}`} style={{ background: 'transparent' }}>
                    <Icono size={40} strokeWidth={1.3} />
                  </div>
                )}
                <div className="tarjeta__nombre">{nodo.nombre}</div>
                <div className="tarjeta__meta">
                  {nodo.tipo === 'CARPETA' ? `${nodo.hijos ?? 0} elementos` : tamano(nodo.bytes)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const flecha = (campo: CampoOrden) =>
    orden.campo === campo ? (orden.ascendente ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : null;

  return (
    <div className="lista" onContextMenu={(e) => alMenu(e, null)}>
      <div className="lista__cabecera">
        <button onClick={() => alOrdenar('nombre')}>Nombre {flecha('nombre')}</button>
        <button onClick={() => alOrdenar('bytes')}>Tamaño {flecha('bytes')}</button>
        <button onClick={() => alOrdenar('actualizadoEn')}>Modificado {flecha('actualizadoEn')}</button>
        <span style={{ textAlign: 'right' }}>Acciones</span>
      </div>

      {nodos.map((nodo, indice) => {
        const { Icono, clase } = iconoDe(nodo);
        return (
          <div
            key={nodo.id}
            className={`fila${seleccion.has(nodo.id) ? ' seleccionada' : ''}${
              cortados.has(nodo.id) ? ' cortada' : ''
            }${destino === nodo.id ? ' destino' : ''}`}
            draggable
            onDragStart={(e) => arrastrar(e, nodo)}
            onClick={(e) => alClicear(e, nodo, indice)}
            onDoubleClick={() => alAbrir(nodo)}
            onContextMenu={(e) => alMenu(e, nodo)}
            {...propsSoltar(nodo)}
          >
            <div className="fila__nombre">
              <span className={`icono-tipo ${clase}`}>
                <Icono size={15} />
              </span>
              <span className="texto">
                {nodo.nombre}
                {/* En los resultados de búsqueda el nombre solo no basta:
                    "informe.txt" puede estar en cualquiera de diez carpetas. */}
                {'ruta' in nodo && <div className="fila__ruta">{(nodo as Nodo & { ruta: string }).ruta}</div>}
              </span>
              {nodo.tipo === 'ARCHIVO' && nodo.version > 1 && (
                <span className="badge badge--gris" title="Versiones publicadas">
                  v{nodo.version}
                </span>
              )}
              {!nodo.heredaPermisos && (
                <Lock size={11} style={{ color: 'var(--gold)', flex: 'none' }} aria-label="Permisos propios" />
              )}
            </div>
            <div className="fila__meta">{nodo.tipo === 'CARPETA' ? '—' : tamano(nodo.bytes)}</div>
            <div className="fila__meta">{fechaRelativa(nodo.actualizadoEn)}</div>
            <div className="fila__acciones">
              {nodo.tipo === 'ARCHIVO' && (
                <button
                  className="btn btn--fantasma btn--icono btn--sm"
                  title="Descargar"
                  onClick={(e) => {
                    e.stopPropagation();
                    alDescargar(nodo);
                  }}
                >
                  <Download size={14} />
                </button>
              )}
              <button
                className="btn btn--fantasma btn--icono btn--sm"
                title="Más acciones"
                onClick={(e) => {
                  e.stopPropagation();
                  alMenu(e, nodo);
                }}
              >
                <MoreVertical size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
